import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Button,
  Flex,
  Select,
  UnstyledButton,
  ThemeIcon,
  Text,
  Box,
  Divider,
  Loader,
  TextInput,
  Stack,
} from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";

export default function BulkCommunicationForm({
  id = null,
  locationId = null,
  title = "",
  variety = "",
  onSuccess = () => {},
  orgId = null,
  effortId = null,
}) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    title: "",
    variety: "",
    message_stream: "",
  });
  useEffect(() => {
    if (id) {
      setFormValues({
        title: title,
      });
    }
  }, [id]);
  const navigate = useNavigate();
  const {
    data: availableVarieties = [],
    isLoading: isLoadingAvailableVarieties,
    isFetching: isFetchingAvailableVarieties,
  } = useQuery({
    enabled: !id,
    queryKey: ["available-varieties"],
    queryFn: () =>
      axios
        .post("/retrieve-bulk-communicator-available-varieties/", {
          campaign_effort_id: effortId,
          location_id: locationId,
          organization_id: orgId,
        })
        .then(({ data }) => data.response[0] || {}),
  });

  function refreshAvailableVarieties() {
    queryClient.invalidateQueries({ queryKey: ["available-varieties"] });
  }

  function onSubmit() {
    setLoading(true);
    if (id) return onUpdate(formValues);
    return onCreate(formValues);
  }

  function onCreate(formData) {
    const req = {
      ...formData,
      location_id: locationId,
      organization_id: orgId,
      entire_db: true,
    };

    if (formValues.variety === "1") {
      req.stream_type = 2;
      req.message_stream = formValues.message_stream;
    } else {
      delete req.message_stream;
    }

    if (effortId) req.campaign_effort_id = effortId;

    axios
      .post("/bulk-communicators/", req)
      .then(({ data }) => {
        toast.success("Created");
        setLoading(false);
        navigate(
          `/bulk-communication/${data.response[0].bulk_communicator_id}/template`
        );
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function onUpdate(formData) {
    const req = {
      ...formData,
    };

    axios
      .put(`/bulk-communicators/${id}/`, req)
      .then(() => setLoading(false))
      .then(() => toast.success("Updated!"))
      .then(() => onSuccess())
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  const emailMarketingAvailable =
    availableVarieties?.email_marketing_available === true;
  const emailNewsAndEventsAvailable =
    availableVarieties?.email_news_and_events_available === true;
  const emailAvailable = emailMarketingAvailable || emailNewsAndEventsAvailable;
  const smsAvailable = availableVarieties?.sms_marketing_available === true;

  const showRefresh =
    !emailAvailable ||
    !smsAvailable ||
    (formValues.variety === "1" &&
      !id &&
      (!emailMarketingAvailable || !emailNewsAndEventsAvailable));

  const submitDisabled = id
    ? !formValues.title
    : !formValues.title ||
      !formValues.variety ||
      (formValues.variety === "1" && !formValues.message_stream) ||
      formValues.title.length > 100;

  return (
    <React.Fragment>
      <Stack gap="xs">
        <TextInput
          required
          label="Title"
          value={formValues.title}
          onChange={(e) =>
            setFormValues({ ...formValues, title: e.target.value })
          }
        />
        {!id && (
          <Select
            required
            label="Variety"
            value={formValues.variety}
            onChange={(e) => setFormValues({ ...formValues, variety: e })}
            placeholder="Select one..."
            data={[
              {
                label: `Email ${emailAvailable ? "" : "(unavailable)"}`,
                value: "1",
                disabled: !emailAvailable,
              },
              {
                label: `Text ${smsAvailable ? "" : "(unavailable)"}`,
                value: "2",
                disabled: !smsAvailable,
              },
            ]}
          />
        )}
        {formValues.variety === "1" && (
          <Select
            required
            label="Message Stream"
            value={formValues.message_stream}
            onChange={(e) =>
              setFormValues({ ...formValues, message_stream: e })
            }
            placeholder="Select one..."
            data={[
              {
                label: "Marketing",
                value: "3",
                disabled: !emailMarketingAvailable,
              },
              // {
              //   label: "Offers",
              //   value: "4",
              // },
              {
                label: "News & Events",
                value: "5",
                disabled: !emailNewsAndEventsAvailable,
              },
            ]}
          />
        )}
        <Button
          fullWidth
          loading={loading}
          onClick={onSubmit}
          disabled={submitDisabled}
        >
          {id ? "Update" : "Create"}
        </Button>
      </Stack>
      {showRefresh && (
        <Box>
          <Divider mt="sm" mb="sm" />
          <UnstyledButton onClick={refreshAvailableVarieties}>
            <Flex gap="xs" align="center">
              <ThemeIcon size="xs" color="gray" variant="subtle">
                {isFetchingAvailableVarieties ? (
                  <Loader size="xs" />
                ) : (
                  <IconRefresh size={16} />
                )}
              </ThemeIcon>
              <Text size="sm">
                Don't see the option you're looking for? Make sure you have the
                necessary access to bulk email or texting and click HERE to
                refresh the options
              </Text>
            </Flex>
          </UnstyledButton>
        </Box>
      )}
    </React.Fragment>
  );
}
